import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";
import { PaperTaskCard } from "./paper/PaperTaskCard.js";
import { useTaskForm } from "./useTaskForm.js";
import "./paper/styles/index.css";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The task create/edit dialog — the paper-notebook card from the Claude
 *  Design mockup, rendered as a full-screen modal overlay. Keeps the standard
 *  dialog a11y contract: focus trap, Escape to close, focus restored on close;
 *  plus Ctrl/⌘+Enter to save. */
export function TaskFormDialog() {
  const { t } = useI18n();
  const form = useTaskForm();
  // "modern" (v2) reskins the same card via the .form-v2 CSS scope — structure,
  // flow and ink animation are shared with the classic paper skin.
  const modern = useSettingsStore((s) => s.desktopFormVariant) === "modern";
  const { open, requestClose, submit, submitting, discardConfirmOpen, cancelDiscard, confirmDiscard } = form;
  const overlayRef = useRef<HTMLDivElement | null>(null);
  // The overlay node doubles as the portal target for the location dropdown
  // (it carries the .paper-form CSS scope). State, not ref, so the first
  // render after mount passes a non-null element down.
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null);

  // Live in refs so the keydown closure always sees the current submit (it
  // closes over the form values of its render) and submitting state without
  // re-binding the listener.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  const cancelDiscardRef = useRef(cancelDiscard);
  cancelDiscardRef.current = cancelDiscard;
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = overlayRef.current;

    const focusScope = (): HTMLElement | null => node?.querySelector<HTMLElement>(".discard-pop") ?? node;
    const focusables = (): HTMLElement[] =>
      Array.from(focusScope()?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Initial focus on the title input (the first focusable is a corner tab).
    (node?.querySelector<HTMLElement>(".title-input") ?? focusables()[0] ?? node)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // An open chip picker owns Escape (its own listener closes it).
        if (node?.querySelector(".chip-pop")) return;
        if (node?.querySelector(".discard-pop")) {
          cancelDiscardRef.current();
          return;
        }
        requestCloseRef.current();
        return;
      }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (node?.querySelector(".discard-pop")) return;
        if (!submittingRef.current) void submitRef.current();
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const els = focusables();
      const scope = focusScope() ?? node;
      if (els.length === 0) { e.preventDefault(); scope.focus(); return; }
      const first = els[0]!;
      const last = els[els.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !scope.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <div
      className={"paper-form" + (modern ? " form-v2" : "")}
      ref={(el) => { overlayRef.current = el; setOverlayEl(el); }}
      role="dialog"
      aria-modal="true"
      aria-label={form.editing ? t("taskForm.editTask") : t("taskForm.newTask")}
      tabIndex={-1}
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <PaperTaskCard form={form} portalEl={overlayEl} />
      {discardConfirmOpen && (
        <div
          className="discard-scrim"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) cancelDiscard(); }}
        >
          <div
            className="discard-pop"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="discard-title"
            aria-describedby="discard-copy"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="discard-mark" aria-hidden="true">!</div>
            <div className="discard-text">
              <h2 id="discard-title">{t("taskForm.discardTitle")}</h2>
              <p id="discard-copy">{t("taskForm.discardCopy")}</p>
            </div>
            <div className="discard-actions">
              <button type="button" className="btn" onClick={cancelDiscard} autoFocus>{t("taskForm.keepEditing")}</button>
              <button type="button" className="btn danger" onClick={confirmDiscard}>{t("taskForm.discard")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
